import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  NOTIFICATION_KINDS,
  notificationKindLabel,
  withSetting,
  type NotificationKind,
  type NotificationSetting,
} from '../domain/notifications';
import { useLoad } from '../hooks/useLoad';
import { useRepositories } from '../hooks/useRepositories';
import { ActionError, LoadingState, StateForError } from '../components/PageStates';
import { ArrowLeft } from 'lucide-react';

const SAVE_FAILED = new Error("Couldn't save. Try again.");

/**
 * `/settings/notifications` — which pushes I get, one switch per kind (020
 * US2). The web sends none itself; the choices apply to my phones. Each
 * switch saves on its own and flips back if the save fails.
 */
export default function NotificationSettingsPage() {
  const { notifications } = useRepositories();
  const loaded = useLoad(() => notifications.settings(), 'notification-settings');
  const [settings, setSettings] = useState<NotificationSetting[] | null>(null);
  const [saving, setSaving] = useState<Set<NotificationKind>>(new Set());
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    if (loaded.status === 'ready') setSettings(loaded.data);
  }, [loaded.status, loaded.data]);

  const toggle = async (kind: NotificationKind, enabled: boolean) => {
    setError(null);
    setSettings((current) => current && withSetting(current, kind, enabled));
    setSaving((s) => new Set(s).add(kind));
    try {
      setSettings(
        await notifications.updateSettings([
          { kind, channel: 'push', collectionId: null, enabled },
        ]),
      );
    } catch {
      setSettings((current) => current && withSetting(current, kind, !enabled));
      setError(SAVE_FAILED);
    } finally {
      setSaving((s) => {
        const next = new Set(s);
        next.delete(kind);
        return next;
      });
    }
  };

  let body;
  if (loaded.status === 'error')
    body = <StateForError error={loaded.error} onRetry={loaded.reload} what="Notifications" />;
  else if (loaded.status === 'loading' || !settings)
    body = <LoadingState label="Loading notification settings" />;
  else
    body = (
      <ul aria-label="Notifications" className="space-y-2">
        {NOTIFICATION_KINDS.map((kind) => {
          const on = settings.find((s) => s.kind === kind)?.enabled ?? true;
          return (
            <li key={kind} className="flex items-center gap-4 rounded-3xl bg-card px-5 py-4">
              <span id={`notify-${kind}`} className="flex-1 font-medium text-foreground">
                {notificationKindLabel(kind)}
              </span>
              <button
                type="button"
                role="switch"
                aria-checked={on}
                aria-labelledby={`notify-${kind}`}
                disabled={saving.has(kind)}
                onClick={() => toggle(kind, !on)}
                className={`relative inline-flex h-7 w-12 shrink-0 items-center rounded-full transition-colors disabled:opacity-60 ${
                  on ? 'bg-primary' : 'bg-input'
                }`}
              >
                <span
                  className={`inline-block size-6 rounded-full bg-background shadow transition-transform ${
                    on ? 'translate-x-5' : 'translate-x-0.5'
                  }`}
                />
              </button>
            </li>
          );
        })}
      </ul>
    );

  return (
    <div className="mx-auto max-w-3xl space-y-8">
      <div>
        <Link
          to="/settings/account"
          className="mb-4 inline-flex items-center gap-1 text-sm font-medium text-muted-foreground hover:text-primary"
        >
          <ArrowLeft className="size-4" />
          Account settings
        </Link>
        <h1 className="text-4xl font-bold tracking-tight text-foreground">Notifications</h1>
        <p className="mt-2 text-muted-foreground">
          These choices apply to your phones. The web app doesn't send notifications.
        </p>
      </div>
      {error !== null && <ActionError error={error} />}
      {body}
    </div>
  );
}
